/**
 * Component for the upload of multiple files to process.
 * Drag'n drop from a file manager is supported.
 *
 * Copyright © Ircam amplify 2024
 */
import { useEffect, useState } from 'react'
import { BiPlusCircle } from 'react-icons/bi'
import { InputCondition, ModuleMimeType } from '../ModuleConfig/ModuleDef'
import FileUploader, { FileUploaderTitle } from './FileUploader'
import { UploadFileInfo } from './UploadFileInfo'

interface MultiFileUploaderProps {
  title: string
  className?: string
  inputData: Record<string, UploadFileInfo>
  addInputFile: (
    inputId: string,
    fileInfo: UploadFileInfo[] | undefined
  ) => void
  inputId: string
  mimeTypes: ModuleMimeType[]
  tip?: string
  maxInputCount: number
  inputCondition?: InputCondition
  disabled?: boolean
}

function MultiFileUploader(props: MultiFileUploaderProps) {
  /**
   * The list of solo-file-upload components.
   */
  const [uploadComponent, setUploadComponent] = useState<JSX.Element[]>([])

  /**
   * The number of requested components to display.
   */
  const [uploaderCount, setUploadCompCount] = useState(1)

  const addUploadField = () => {
    setUploadCompCount(uploaderCount + 1)
  }

  useEffect(() => {
    let count = Math.max(
      uploaderCount,
      Object.keys(props.inputData || {}).length
    )
    if (props.maxInputCount) {
      count = Math.min(props.maxInputCount, count)
    }

    const updatedComponents = Array.from({ length: count }, (_, index) => {
      const fileKey = `${props.inputId}_${index + 1}`
      const file = props.inputData?.[fileKey]

      return (
        <div
          className={`uploadContainerBorder MultiFileUploader ${props.className ?? ''}`}
          key={fileKey}
        >
          <FileUploader
            title=""
            mimeTypes={props.mimeTypes}
            inputData={props.inputData}
            addInputFile={props.addInputFile}
            inputFileToUpload={file}
            inputId={fileKey}
            inputCondition={InputCondition.Mandatory}
            disabled={props.disabled}
          />
        </div>
      )
    })

    setUploadComponent(updatedComponents)
    setUploadCompCount(count)
  }, [props.inputData, uploaderCount])

  return (
    <div>
      <FileUploaderTitle
        className="titleBar"
        inputCondition={props.inputCondition}
        title={props.title}
        tip={props.tip}
      />

      {uploadComponent}

      {uploaderCount < props.maxInputCount && (
        <button
          type="button"
          className="linkBtn2"
          onClick={() => addUploadField()}
        >
          <BiPlusCircle />
          Add more files
        </button>
      )}
    </div>
  )
}

export default MultiFileUploader
